import { useCallback, useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getContactLeads } from "../utils/auth";
const LeadsTable = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [groupByCompany, setGroupByCompany] = useState(false);

  const itemsPerPage = 5;

 const fetchLeads = useCallback(async () => {
  try {
    setLoading(true);
    setError("");
    const res = await getContactLeads();
    setLeads(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    setError("Failed to fetch leads");
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, groupByCompany]);

  // 🔍 Filter
  const filteredLeads = useMemo(() => {
    return leads.filter((lead) =>
      Object.values(lead).some((val) =>
        String(val).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [leads, search]);

  // 📊 Sorting
  const sortedLeads = useMemo(() => {
    let sortable = [...filteredLeads];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = String(a[sortConfig.key] ?? "").toLowerCase();
        const valB = String(b[sortConfig.key] ?? "").toLowerCase();

        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      });
    }
    return sortable;
  }, [filteredLeads, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  // 📄 Pagination
  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const paginatedLeads = sortedLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 📥 Export to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(sortedLeads);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(file, "leads.xlsx");
  };

  // 📅 Format Date
  const formatDate = (date) =>
    date ? new Date(date).toLocaleString("en-IN") : "";

  const renderStatus = (value) => (
    <span className={`badge status-pill ${value ? "bg-success" : "bg-danger"}`}>
      {value ? "Yes" : "No"}
    </span>
  );

  // 🧠 Group by Company
  const groupedData = useMemo(() => {
    if (!groupByCompany) return null;

    return sortedLeads.reduce((acc, lead) => {
      const company = lead.company || "Unknown company";
      acc[company] = acc[company] || [];
      acc[company].push(lead);
      return acc;
    }, {});
  }, [sortedLeads, groupByCompany]);

  return (
    <div>

      <div className="dashboard-toolbar">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search leads..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="dashboard-actions">
          <button className="btn btn-primary" onClick={exportToExcel} disabled={sortedLeads.length === 0}>
            Export Excel
          </button>

          <button
            className="btn btn-outline-secondary"
            onClick={() => setGroupByCompany(!groupByCompany)}
          >
            {groupByCompany ? "Ungroup" : "Group by Company"}
          </button>
        </div>
      </div>

      {loading && <div className="dashboard-loading">Loading leads...</div>}
      {!loading && error && <div className="dashboard-error">{error}</div>}

      {!loading && !groupByCompany && (
        <>
          <div className="table-responsive">
            <table className="table table-hover dashboard-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th className="sortable-heading" onClick={() => handleSort("fullName")}>Name</th>
                  <th className="sortable-heading" onClick={() => handleSort("title")}>Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th className="sortable-heading" onClick={() => handleSort("company")}>Company</th>
                  <th>Message</th>
                  <th>Owner</th>
                  <th>User</th>
                  <th className="sortable-heading" onClick={() => handleSort("createdAt")}>Created</th>
                </tr>
              </thead>

              <tbody>
                {paginatedLeads.map((lead, i) => (
                  <tr key={lead.id}>
                    <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                    <td>{lead.fullName}</td>
                    <td>{lead.title}</td>
                    <td>{lead.email}</td>
                    <td>{lead.phone}</td>
                    <td>{lead.company}</td>
                    <td>{lead.message}</td>
                    <td>{renderStatus(lead.notifiedOwner)}</td>
                    <td>{renderStatus(lead.notifiedUser)}</td>
                    <td>{formatDate(lead.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dashboard-pagination">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`btn ${
                  currentPage === i + 1 ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </>
      )}

      {!loading && groupByCompany &&
        Object.entries(groupedData || {}).map(([company, items]) => (
          <div key={company} className="dashboard-card mb-3">
            <h5 className="fw-bold">{company}</h5>
            <ul className="list-group">
              {items.map((lead) => (
                <li key={lead.id} className="list-group-item">
                  {lead.fullName} - {lead.email}
                </li>
              ))}
            </ul>
          </div>
        ))}
      {!loading && sortedLeads.length === 0 && !error && (
        <div className="dashboard-empty">No leads found.</div>
      )}
    </div>
  );
};

export default LeadsTable;
